import { useMutation, useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import { googleFontQueryKeyGenerator } from "@/react/components/SettingsApp/Site/Pages/Edit/PageSettings/CustomFonts/GoogleFonts/hooks/useGetGoogleFonts";
import { pageQueryKey } from "@/react/components/SettingsApp/Site/Pages/hooks/usePage";
import type { GoogleFontWithWeights } from "@/react/components/shared/BuilderUI/Store/types";
import { useToast } from "@/react/components/shared/uikit/ToastV2";
import type { ApiError } from "@/react/config/CustomErrors";
import { reactQueryPost } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";

interface CreateGoogleFontsFromStoreRequest {
  googleFonts: GoogleFontWithWeights[];
  pageId: number;
}

export const useCreateGoogleFontsFromStore = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<void, ApiError, CreateGoogleFontsFromStoreRequest>({
    mutationFn: async ({ pageId, googleFonts }) => {
      // TODO: Update backend to accept an array of fonts instead of doing this in the frontend
      const createPromises = googleFonts.map(font => {
        const payload = {
          font_family: font.family,
          variants: Array.from(font.weights).join(","),
          fallback_font:
            "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
          font_type: "google" as const,
        };

        return reactQueryPost(
          internalApi.settings.sites.pages.custom_fonts.create({ pageId }),
          payload,
        );
      });

      await Promise.all(createPromises);
    },
    onSuccess: (_, { pageId }) => {
      void queryClient.invalidateQueries({
        queryKey: googleFontQueryKeyGenerator(pageId),
      });
      void queryClient.invalidateQueries({
        queryKey: pageQueryKey(pageId),
      });
      toast.success(t("changes_saved"));
    },
    onError: () => {
      toast.error(t("something_went_wrong"));
    },
  });
};
